/**
 * What the browser tab, the history entry and the bookmark are called.
 *
 * The app shipped with ONE title — the literal `<title>` in `index.html` — so
 * every page was "Authe — Dashboard". An analyst works several cases at once in
 * several tabs, which is the normal shape of this job, and none of the tabs said
 * which case it held; history and bookmarks were equally useless.
 *
 * Two layers, because a page's name has two halves:
 *
 *   - the PAGE, derived from the route. Taken from `NAV_ITEMS` where the route is
 *     in the sidebar, so the tab and the nav entry can never disagree, plus a map
 *     for the detail routes that are not nav destinations.
 *   - the SUBJECT, set by the page once it knows what it is looking at — a case's
 *     name, an address, a transaction. `useDocumentSubject` is that seam; a page
 *     that never calls it simply keeps the page name.
 *
 * Kept out of the route table on purpose: the subject is known by the component
 * that loaded it, not by the route, and this router's routes have no loaders.
 */
import * as React from "react";
import { useRouterState } from "@tanstack/react-router";
import { NAV_ITEMS } from "@/config";

const APP_NAME = "Authe";

/**
 * Detail + utility routes, by route path. Nav destinations are not repeated
 * here — they come from {@link NAV_ITEMS}, which is where their names already
 * live.
 */
const ROUTE_TITLES: Record<string, string> = {
  "/transactions/$id": "Transaction",
  "/addresses/$id": "Address",
  "/investigations/$id": "Case",
  "/entities/$id": "Entity",
  "/groups/$id": "Group",
  "/tags/$id": "Tag",
  "/labels/$id": "Label",
  "/contracts/$chainId/$address": "Contract",
  "/pools/$chainId/$address": "Pool",
  "/watches/$id": "Watch",
  "/pipelines/$id": "Pipeline",
  "/analytics/$dashboardId": "Dashboard",
  "/analytics/explorer": "Explorer",
  "/analytics/protocols": "Protocol confidence",
  "/settings": "Settings",
  "/settings/billing": "Billing",
  "/settings/exports": "Exports",
  "/profile": "Profile",
  "/teams/accept-invite": "Team invitation",
};

/** The page name for a matched route path, or undefined when nothing claims it. */
export function pageTitleFor(routePath: string | undefined): string | undefined {
  if (!routePath) return undefined;
  const nav = NAV_ITEMS.find((item) => item.to === routePath);
  if (nav) return nav.label;
  return ROUTE_TITLES[routePath];
}

/** `subject · page — Authe`, with each half optional. */
export function composeTitle(page: string | undefined, subject: string | undefined): string {
  const head = [subject, page].filter((part): part is string => Boolean(part && part.trim()));
  return head.length > 0 ? `${head.join(" · ")} — ${APP_NAME}` : `${APP_NAME} — Dashboard`;
}

interface SubjectSlot {
  setSubject: (subject: string | undefined) => void;
}

const SubjectContext = React.createContext<SubjectSlot | null>(null);

/**
 * Name the thing this page is about — a case's name, an address, a hash.
 *
 * Pass `undefined` while it is still loading: the tab then shows the page name
 * alone rather than a stale subject or a flash of the previous one. The slot is
 * cleared on unmount, so a page that navigates away never leaves its name behind.
 */
export function useDocumentSubject(subject: string | undefined): void {
  const slot = React.useContext(SubjectContext);
  React.useEffect(() => {
    if (!slot) return;
    slot.setSubject(subject);
    return () => slot.setSubject(undefined);
  }, [slot, subject]);
}

/**
 * Keeps `document.title` in step with the route and the current subject.
 *
 * Renders its children untouched — it is a behaviour, not a layout.
 */
export function DocumentTitleProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [subject, setSubject] = React.useState<string | undefined>(undefined);
  // The deepest matched route that names a page: a detail route wins over the
  // layout route it is nested in.
  const page = useRouterState({
    select: (state) => {
      for (let i = state.matches.length - 1; i >= 0; i -= 1) {
        const title = pageTitleFor(state.matches[i]?.routeId as string | undefined);
        if (title) return title;
      }
      return undefined;
    },
  });

  const slot = React.useMemo<SubjectSlot>(() => ({ setSubject }), []);

  React.useEffect(() => {
    document.title = composeTitle(page, subject);
  }, [page, subject]);

  return <SubjectContext.Provider value={slot}>{children}</SubjectContext.Provider>;
}
